/**
 * CollarValidator · 7-status validation of drillhole collars against
 * the approved project boundary. Delegates to
 *   POST /api/projects/{pid}/gis/collars/validate
 *
 * Priority 3 requirement:
 *   - Transform boundary + collars into same confirmed CRS before test
 *   - Show tested coordinates, CRS, distance-to-boundary, reason
 *   - 7 statuses: inside, on_boundary, near_boundary, outside,
 *                 crs_unknown, transformation_failed, invalid_coordinate
 *   - Never move collars automatically
 */
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Compass, ArrowClockwise, MapPin, Warning, CheckCircle, XCircle, Info,
  Lightning, ShieldCheck,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useCollarValidationQuery } from "./useCollarValidation";

const STATUS_META = {
  inside:                    { tone: "emerald", Icon: CheckCircle, label: "Inside" },
  on_boundary:              { tone: "sky",     Icon: MapPin,        label: "On boundary" },
  near_boundary:            { tone: "amber",   Icon: MapPin,        label: "Near boundary" },
  outside:                   { tone: "rose",    Icon: XCircle,        label: "Outside" },
  crs_unknown:              { tone: "amber",   Icon: Warning,        label: "CRS unknown" },
  transformation_failed:     { tone: "rose",    Icon: Warning,        label: "Transformation failed" },
  invalid_coordinate:        { tone: "rose",    Icon: XCircle,        label: "Invalid coordinate" },
};
const TONE_CLS = {
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
  sky:     "border-sky-200 bg-sky-50 text-sky-800",
  amber:   "border-amber-200 bg-amber-50 text-amber-800",
  rose:    "border-rose-200 bg-rose-50 text-rose-800",
};

export default function CollarValidator({ pid }) {
  const [nearTol, setNearTol] = useState(25);
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState("all");
  const [diagnosis, setDiagnosis] = useState(null);
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);

  // Feb 2026 · Iter81 · TanStack-cached validation. The query is shared
  // with GISStudio (for the Validation-tab pill badge) so the tab
  // hop-cycle never re-hits the API.
  const validationQ = useCollarValidationQuery(pid, nearTol);
  const result = validationQ.data;

  const runMut = useMutation({
    mutationFn: () =>
      api.post(`/projects/${pid}/gis/collars/validate?near_tolerance_m=${nearTol}`).then((r) => r.data),
    onSuccess: (r) => {
      // Push the fresh result into the shared cache so the pill badge
      // updates in-place (no stale flash).
      qc.setQueryData(["collar-validation", pid, nearTol], r);
      toast.success(`Validated ${r.total} collars`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Validation failed"),
  });

  const diagnoseMut = useMutation({
    mutationFn: () =>
      api.post(`/projects/${pid}/gis/collars/diagnose`).then((r) => r.data),
    onSuccess: (r) => {
      setDiagnosis(r);
      setSelectedCandidateId(r?.top?.id || null);
      if (r?.candidates?.length) {
        toast.success(`Diagnosis ready — ${r.candidates.length} candidate(s)`);
      } else {
        toast.info(r.diagnosis || "No candidates found");
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Diagnosis failed"),
  });

  const applyMut = useMutation({
    mutationFn: (cand) => api.post(
      `/projects/${pid}/gis/collars/apply-correction`,
      {
        candidate_id: cand.id,
        source_epsg:   cand.source_epsg,
        kind:          cand.kind,
        swap_xy:       !!cand.swap_xy,
        scale_factor:  Number(cand.scale_factor || 1.0),
      },
    ).then((r) => r.data),
    onSuccess: (r) => {
      toast.success(`Applied correction — ${r.rows_corrected} rows updated`);
      // Refresh the shared cache — the pill badge + KPI cards animate.
      qc.invalidateQueries({ queryKey: ["collar-validation", pid] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Apply failed"),
  });

  // If the user changes the near-tolerance we force a refresh so the
  // cache is aligned with the new bucket. TanStack Query already keys
  // by nearTol so the previous value stays cached — cheap round trip.
  useEffect(() => {
    if (result && result.near_tolerance_m !== nearTol) {
      validationQ.refetch();
    }
  }, [nearTol, result, validationQ]);

  const filtered = useMemo(() => {
    const rows = result?.results || [];
    return statusFilter === "all" ? rows : rows.filter((r) => r.status === statusFilter);
  }, [result, statusFilter]);

  return (
    <div className="space-y-4" data-testid="collar-validator-panel">
      <section className="rounded-lg bg-white border border-slate-200 p-5">
        <div className="flex items-center gap-3 mb-3">
          <Compass size={16} className="text-brand-blue" weight="duotone" />
          <div className="flex-1 min-w-0">
            <h2 className="font-heading font-bold text-[15px] text-slate-900">Collar vs Boundary Validation</h2>
            <p className="text-[11.5px] text-slate-500">
              Reprojects each collar into the boundary CRS before testing. Collars are never moved automatically — this is advisory only.
            </p>
          </div>
        </div>
        <div className="flex items-end gap-3 mb-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">Near-boundary tolerance</label>
            <div className="flex items-center gap-1">
              <input type="number" min="0" max="1000" step="1"
                       value={nearTol}
                       onChange={(e) => setNearTol(Number(e.target.value) || 0)}
                       data-testid="input-near-tolerance"
                       className="w-24 h-9 rounded-md border border-slate-200 bg-white px-2 text-[12.5px] font-mono" />
              <span className="text-[11px] text-slate-500">m</span>
            </div>
          </div>
          <Button size="sm" onClick={() => runMut.mutate()} disabled={runMut.isPending}
                     data-testid="btn-run-collar-validation"
                     className="bg-brand-blue hover:bg-brand-blue/90 text-white text-[11.5px] gap-1">
            {runMut.isPending ? <ArrowClockwise size={12} className="animate-spin" /> : <Compass size={12} />}
            Run validation
          </Button>
          <Button size="sm" variant="outline"
                     onClick={() => diagnoseMut.mutate()} disabled={diagnoseMut.isPending}
                     data-testid="btn-diagnose-crs-mismatch"
                     className="text-[11.5px] gap-1 border-amber-300 text-amber-800 hover:bg-amber-50">
            {diagnoseMut.isPending
              ? <ArrowClockwise size={12} className="animate-spin" />
              : <Lightning size={12} weight="fill" />}
            Diagnose CRS mismatch
          </Button>
        </div>

        {result && (
          <div className="border-t border-slate-100 pt-3 space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-[11px]" data-testid="collar-validation-summary">
              <Kpi label="Total"                value={result.total} tone="slate" />
              <Kpi label="Inside boundary"      value={result.summary?.inside ?? 0} tone="emerald" />
              <Kpi label="Outside boundary"     value={result.summary?.outside ?? 0} tone="rose" />
              <Kpi label="Invalid coordinates" value={result.summary?.invalid_coordinate ?? 0} tone="amber" />
              <Kpi label="Possible CRS mismatch"
                    value={(result.summary?.crs_unknown ?? 0)
                             + (result.summary?.transformation_failed ?? 0)}
                    tone="amber" />
            </div>

            {/* Status pills */}
            <div className="flex items-center gap-1.5 flex-wrap" data-testid="collar-status-filters">
              <FilterPill active={statusFilter === "all"} label={`All (${result.total})`}
                              onClick={() => setStatusFilter("all")} testId="filter-all" />
              {Object.entries(result.summary || {}).map(([k, v]) => (
                <FilterPill key={k} active={statusFilter === k}
                                 label={`${STATUS_META[k].label} (${v})`}
                                 tone={STATUS_META[k].tone}
                                 onClick={() => setStatusFilter(k)}
                                 testId={`filter-${k}`} />
              ))}
            </div>

            {/* Table */}
            <div className="overflow-x-auto rounded-md border border-slate-200">
              <table className="w-full text-[11px]" data-testid="collar-validation-table">
                <thead className="bg-slate-50 text-slate-500 uppercase tracking-widest text-[9.5px]">
                  <tr>
                    <th className="text-left px-2 py-1.5">Hole ID</th>
                    <th className="text-left px-2 py-1.5">Status</th>
                    <th className="text-left px-2 py-1.5">Input (x, y)</th>
                    <th className="text-left px-2 py-1.5">Source CRS</th>
                    <th className="text-left px-2 py-1.5">Tested (lon, lat)</th>
                    <th className="text-left px-2 py-1.5">Distance</th>
                    <th className="text-left px-2 py-1.5">Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.slice(0, 500).map((r) => {
                    const meta = STATUS_META[r.status] || STATUS_META.invalid_coordinate;
                    return (
                      <tr key={r.hole_id} data-testid={`collar-row-${r.hole_id}`}>
                        <td className="px-2 py-1.5 font-mono">{r.hole_id}</td>
                        <td className="px-2 py-1.5">
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border ${TONE_CLS[meta.tone]}`}>
                            <meta.Icon size={9} weight="fill" /> {meta.label}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 font-mono">
                          {r.input_x != null ? `${Number(r.input_x).toFixed(3)}, ${Number(r.input_y).toFixed(3)}` : "—"}
                        </td>
                        <td className="px-2 py-1.5 font-mono">{r.source_crs || "—"}</td>
                        <td className="px-2 py-1.5 font-mono">
                          {r.tested_lon != null ? `${Number(r.tested_lon).toFixed(4)}, ${Number(r.tested_lat).toFixed(4)}` : "—"}
                        </td>
                        <td className="px-2 py-1.5 font-mono">
                          {r.distance_to_boundary_m != null ? `${Number(r.distance_to_boundary_m).toFixed(1)} m` : "—"}
                        </td>
                        <td className="px-2 py-1.5 text-slate-600">{r.reason}</td>
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr><td colSpan={7} className="px-2 py-6 text-center text-slate-400 italic">No collars match this filter.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {/* Iter81 · CRS Diagnosis Panel — ranked correction candidates */}
      {diagnosis && (
        <section className="rounded-lg bg-white border border-amber-300 p-5 shadow-sm"
                     data-testid="collar-diagnosis-panel">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center text-white shrink-0">
              <Lightning size={18} weight="fill" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-heading font-black text-[16px] text-slate-900">
                CRS Mismatch Diagnosis
              </h2>
              <p className="text-[12px] text-slate-600 mt-0.5" data-testid="collar-diagnosis-summary">
                {diagnosis.diagnosis}
              </p>
              <div className="mt-1 text-[10.5px] text-slate-500">
                {diagnosis.total_collars} collars · {diagnosis.raw_summary?.valid || 0} valid ·{" "}
                {diagnosis.raw_summary?.invalid || 0} invalid ·
                Boundary CRS <b className="font-mono">{diagnosis.boundary_crs || "—"}</b> ·
                Country <b>{diagnosis.country || "—"}</b>
              </div>
            </div>
          </div>

          {(diagnosis.candidates || []).length === 0 ? (
            <div className="text-[11.5px] italic text-slate-500 border-t border-amber-200 pt-3">
              No candidate transformations available. Please verify the boundary CRS and
              re-upload the collar file. Manual editing may be required.
            </div>
          ) : (
            <>
              <div className="border-t border-amber-200 pt-3 space-y-2"
                        data-testid="collar-candidate-list">
                {diagnosis.candidates.map((c) => {
                  const total = c.total || 1;
                  const insideFrac = c.inside / total;
                  const pctOk = Math.round(insideFrac * 100);
                  const isSel = selectedCandidateId === c.id;
                  return (
                    <label key={c.id}
                              data-testid={`collar-candidate-${c.id}`}
                              className={`flex items-start gap-2 rounded-md border cursor-pointer p-2 text-[11.5px]
                                              ${isSel ? "border-brand-blue bg-blue-50/60" : "border-slate-200 bg-white hover:border-brand-blue/40"}`}>
                      <input type="radio" checked={isSel}
                                onChange={() => setSelectedCandidateId(c.id)}
                                data-testid={`collar-candidate-radio-${c.id}`}
                                className="mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-800">{c.label}</span>
                          <span className={`inline-flex items-center rounded-sm border px-1.5 py-0.5 text-[9.5px] uppercase tracking-widest font-bold
                                                ${c.confidence >= 0.75 ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                                                  : c.confidence >= 0.5 ? "border-amber-300 bg-amber-50 text-amber-800"
                                                  : "border-rose-300 bg-rose-50 text-rose-800"}`}>
                            Confidence {Math.round((c.confidence || 0) * 100)}%
                          </span>
                          <span className="inline-flex items-center rounded-sm border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[9.5px] font-mono">
                            {c.inside} / {total} inside ({pctOk}%)
                          </span>
                        </div>
                        <div className="mt-1 text-[10.5px] text-slate-500 font-mono">
                          {c.cause || c.reason}
                        </div>
                        {(c.preview || []).length > 0 && (
                          <div className="mt-1 text-[10px] text-slate-500 font-mono truncate">
                            Sample →{" "}
                            {c.preview.map((p, i) => (
                              <span key={i}>
                                ({p.lon}, {p.lat}){i < c.preview.length - 1 ? " · " : ""}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>

              <div className="mt-3 flex items-center gap-2 border-t border-amber-200 pt-3">
                <Button size="sm"
                            onClick={() => {
                              const cand = (diagnosis.candidates || [])
                                    .find((c) => c.id === selectedCandidateId)
                                    || diagnosis.top;
                              if (cand) applyMut.mutate(cand);
                            }}
                            disabled={applyMut.isPending || !selectedCandidateId}
                            data-testid="btn-apply-collar-correction"
                            className="bg-amber-600 hover:bg-amber-700 text-white text-[11.5px] gap-1">
                  {applyMut.isPending
                    ? <ArrowClockwise size={12} className="animate-spin" />
                    : <ShieldCheck size={12} weight="fill" />}
                  Apply Suggested Correction
                </Button>
                <span className="text-[10.5px] text-slate-500">
                  Original collar rows are archived. Correction saves as a new version.
                </span>
              </div>
            </>
          )}
        </section>
      )}

      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600 flex items-start gap-2">
        <Info size={11} className="text-slate-400 mt-0.5" />
        <div>
          <b>Why some collars may appear outside:</b> a collar known to be inside the licence may be flagged Outside if its CRS was misidentified during upload. Confirm the project EPSG (Step 3 of the wizard) and the boundary source CRS, then re-run validation. Collars are never moved by SRES — you must fix source data.
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, tone = "slate" }) {
  return (
    <div className={`rounded-md border p-2 ${TONE_CLS[tone] || "border-slate-200 bg-slate-50 text-slate-800"}`}>
      <div className="text-[9.5px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-[13px] font-mono font-bold">{value}</div>
    </div>
  );
}

function FilterPill({ active, label, tone = "slate", onClick, testId }) {
  const activeCls = active
    ? "bg-brand-blue text-white border-brand-blue"
    : "bg-white text-slate-700 border-slate-200 hover:border-brand-blue/40";
  return (
    <button type="button" onClick={onClick} data-testid={testId}
                className={`inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 text-[10px] uppercase tracking-widest ${activeCls}`}>
      {label}
    </button>
  );
}
